import {
  ApplicationConfig,
  inject,
  Injector,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { IMAGE_CONFIG } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

// Azure AD Authentication
import { MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { MSALInstanceFactory, MsalGuardConfigurationFactory, MSALInterceptorConfigFactory, msalInitializerFactory } from './auth/msal.config';
import { CustomMsalInterceptor } from './auth/custom-msal.interceptor';
import { AuthErrorInterceptor } from './core/auth-error.interceptor';
import { CloudflareInterceptor } from './core/cloudflare.interceptor';

import { routes } from './app.routes';
import { faroInitializer } from '../faro-initializer';
import { maintenanceAppInitializerFactory } from './core/maintenance-initializer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'disabled',
        anchorScrolling: 'enabled'
      })
    ),
    { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true } },
    provideHttpClient(withInterceptorsFromDi()),
    // MSAL Providers (instance must exist before initialize runs)
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MsalGuardConfigurationFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    provideAppInitializer(() => msalInitializerFactory(inject(MsalService))()),
    provideAppInitializer(() => maintenanceAppInitializerFactory(inject(Injector))()),
    // Cloudflare interceptor - must be first to add browser-like headers
    // and handle Cloudflare cookie challenges
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CloudflareInterceptor,
      multi: true
    },
    // Use custom interceptor instead of default MSAL interceptor
    // This handles both internal auth and Azure AD auth
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomMsalInterceptor,
      multi: true
    },
    // Auth error interceptor to handle 401 errors and auto-logout
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true
    },
    provideAppInitializer(async () => {
      await faroInitializer()();
    }),
    MsalGuard,
    MsalBroadcastService
  ]
};
